import { Component, OnInit } from '@angular/core';

import { FileService } from '../services/file.service';
import { LocalStorageService } from '../services/local-storage.service';
import { FileData, UserStatus } from '../constants/app.constants';
import { ApplicationResponseData, IFileData, KeyValueData } from '../domains/common.domain';

import { DocumentsListData, TechnicianDocumentsData } from './documents.domain';
import { DocumentsService } from './documents.service';
import { OrderProcessService } from '../jurisdiction/order-process/order-process.service';
import { DocumentDataObject, OrderProcessData, OrderProcessResponse } from '../jurisdiction-overview/jurisdiction-overview.domain';
import { ErrorService } from '../services/error/error.service';
import { AllDropdownKeys } from '../jurisdiction/jurisdiction.constants';
import { EnvironmentService } from '../services/environment.service';
import { MandatoryDocumentsPerCommodity } from '../interface/config.interface';

@Component({
  providers: [DocumentsService, OrderProcessService],
  selector: 'app-documents',
  styleUrls: ['./documents.component.scss'],
  templateUrl: 'documents.component.html',
})
export class DocumentsComponent implements OnInit {
  documentsData: TechnicianDocumentsData[];
  documentsList: DocumentsListData[];
  mandatoryDocumentsPerCommodity: MandatoryDocumentsPerCommodity;
  executorActionsKey: string;
  fileData: IFileData;
  isLoading: boolean;
  userRole: string;
  userStatus: KeyValueData;
  showSafetyTechnologyAuthorizationDocument: boolean;

  orderProcessData: OrderProcessData[];
  invoiceDocuments: DocumentDataObject[];
  offerDocuments: DocumentDataObject[];
  orderDocuments: DocumentDataObject[];

  constructor(
    private documentsService: DocumentsService,
    private errorService: ErrorService,
    private fileService: FileService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private orderProcessService: OrderProcessService
  ) {}

  ngOnInit(): void {
    this.mandatoryDocumentsPerCommodity = this.environmentService.mandatoryDocumentsPerCommodity;
    this.setRequiredDocuments();
    this.executorActionsKey = AllDropdownKeys.ExecutorType;
    this.fileData = FileData;
    this.documentsData = [];
    this.userRole = this.localStorageService.getItem('role') || '';
    this.userStatus = UserStatus;
    this.isLoading = false;
    this.showSafetyTechnologyAuthorizationDocument = this.environmentService.showSafetyTechnologyAuthorizationDocument;

    if (
      this.userRole.toUpperCase() === this.userStatus.GUESTTECHNICIAN ||
      this.userRole.toUpperCase() === this.userStatus.STANDARDTECHNICIAN
    ) {
      this.getAllDocuments();
    }
    if (this.userRole.toUpperCase() === this.userStatus.CUSTOMER) {
      this.orderProcessData = null;
      this.invoiceDocuments = [];
      this.offerDocuments = [];
      this.orderDocuments = [];
      this.getOrderProcess();
    }
  }

  getAllDocuments(): void {
    const requestData = {
      technician_id: this.localStorageService.getItem('technician_id'),
    };
    this.isLoading = true;
    this.documentsService.getData(requestData).subscribe((response: ApplicationResponseData) => {
      this.isLoading = false;
      this.documentsData = (response.technician_application?.length && response.technician_application[0].document) || [];
      this.documentsData = this.documentsData.map(data => {
        if (data.filesize.length > 3) {
          data.filesize = (Number(data.filesize) / 1000).toFixed(2) + ' kb';
        } else {
          data.filesize = data.filesize + ' bytes';
        }
        return data;
      });
    });
  }

  getFileName(name: string): string {
    const fileObject = this.documentsData.filter(data => data.name === name);
    if (fileObject.length) {
      return `${fileObject[0].filename} (${fileObject[0].filesize})`;
    }
    return '';
  }

  getOrderProcess(): void {
    this.isLoading = true;
    this.orderProcessService.getAllOrderProcesses().subscribe(
      (response: OrderProcessResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);

        if (response.orderprocess && response.orderprocess.length) {
          this.orderProcessData = response.orderprocess;
          this.orderDocuments = this.prepareDocumentsList('order');
          this.offerDocuments = this.prepareDocumentsList('offer');
          this.invoiceDocuments = this.prepareDocumentsList('invoice');
        }
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  verifyFilename(name: string): boolean {
    let isFileNamePresent = true;

    Object.keys(this.fileData).forEach(key => {
      isFileNamePresent = isFileNamePresent && name !== this.fileData[key];
    });

    return isFileNamePresent;
  }

  checkForAvailableDocuments(name: string): boolean {
    return this.fileService.checkForEnabledAvailableDocuments(this.documentsData, name);
  }

  downloadFile(name: string): void {
    const object = this.getDimensionsByFilter(name);
    if (object !== null && object !== undefined && object.length > 0) {
      this.downloadSelectedFile(object[0].document_id, object[0].filename);
    }
  }

  getDimensionsByFilter(name: string): TechnicianDocumentsData[] {
    return this.fileService.getDimensionsByFilter(this.documentsData, name);
  }

  downloadSelectedFile(documentId: string, fileName?: string): void {
    this.fileService.readDocument(documentId).subscribe((response: Blob) => {
      this.fileService.downloadDocument(response, fileName);
    });
  }

  downloadDocument(documentId: string, fileName?: string): void {
    this.orderProcessService.readDocument(documentId).subscribe((response: Blob) => {
      this.fileService.downloadDocument(response, fileName);
    });
  }

  showDocumentBasedOnVisibilityFlag(visibilityFlag) {
    return visibilityFlag != undefined && this[visibilityFlag] != undefined ? this[visibilityFlag] : true;
  }

  private prepareDocumentsList(documentType: string): DocumentDataObject[] {
    const returnList: DocumentDataObject[] = [];
    this.orderProcessData.forEach((item: OrderProcessData) => {
      if (item[documentType]?.document) {
        const documentsList = item[documentType].document.map((obj: DocumentDataObject) => {
          obj.application_ext_id = item.application_ext_id ?? '';
          obj.application_id = item.application_id;
          return obj;
        });
        returnList.push(...documentsList);
      }
    });
    returnList.sort((a: DocumentDataObject, b: DocumentDataObject) => Number(b.application_id) - Number(a.application_id));
    return returnList;
  }

  private setRequiredDocuments() {
    const takeMandatoryDocumentsTypes = new Set([
      ...this.mandatoryDocumentsPerCommodity.power.documentsCompany,
      ...this.mandatoryDocumentsPerCommodity.gas.documentsCompany,
      ...this.mandatoryDocumentsPerCommodity.water.documentsCompany,
      ...this.mandatoryDocumentsPerCommodity.districtHeating.documentsCompany,
    ]);

    const specialistMandatoryDocumentsTypes = new Set([
      ...this.mandatoryDocumentsPerCommodity.power.documentsSpecialist,
      ...this.mandatoryDocumentsPerCommodity.gas.documentsSpecialist,
      ...this.mandatoryDocumentsPerCommodity.water.documentsSpecialist,
      ...this.mandatoryDocumentsPerCommodity.districtHeating.documentsSpecialist,
    ]);

    if (this.environmentService.showSafetyTechnologyAuthorizationDocument) {
      specialistMandatoryDocumentsTypes.add('welding_certificate_confirmation_not_required');
    }

    this.documentsList = [
      {
        id: 'company-documents',
        section: 'Pflichtdokumente Unternehmen',
        data: takeMandatoryDocumentsTypes,
      },
      {
        id: 'installer-documents',
        section: 'Pflichtdokumente Installateur',
        data: specialistMandatoryDocumentsTypes,
      },
    ];
  }
}
